import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * State manager for persisting testify state
 */
class TestifyState {
  constructor(sectionId) {
    this.key = `testify_state_${sectionId}`;
  }

  save(state) {
    try {
      sessionStorage.setItem(this.key, JSON.stringify({
        ...state,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.warn('State kaydetme hatası:', e);
    }
  }

  load() {
    try {
      const data = sessionStorage.getItem(this.key);
      if (!data) return null;
      
      const state = JSON.parse(data);
      if (Date.now() - state.timestamp > 30 * 60 * 1000) {
        this.clear();
        return null;
      }
      return state;
    } catch {
      return null;
    }
  }

  clear() {
    try {
      sessionStorage.removeItem(this.key);
    } catch (e) {}
  }
}

/**
 * Product Testify - Questionnaire-based product discovery
 * @extends Component
 */
export class ProductTestify extends Component {
  requiredRefs = ['cardStack'];

  /** @type {TestifyState} */
  stateManager;

  // Questionnaire state
  /** @type {Array<{id: string, type: 'yes_no' | 'multiple_choice', question: string, options?: Array<{text: string, keywords: string[]}>, yesKeywords?: string[], noKeywords?: string[]}>} */
  questions = [];
  
  /** @type {number} */
  currentQuestionIndex = 0;
  
  /** @type {string[]} */
  collectedKeywords = [];
  
  /** @type {boolean} */
  questionnaireComplete = false;

  // Product state
  /** @type {Array<{id: string, variantId: string, title: string, price: string, comparePrice: string, image: string, url: string, tags: string[]}>} */
  allProducts = [];
  
  /** @type {Array<{id: string, variantId: string, title: string, price: string, comparePrice: string, image: string, url: string, tags: string[]}>} */
  filteredProducts = [];
  
  /** @type {number} */
  currentIndex = 0;
  
  /** @type {Array<{productId: string, variantId: string, action: 'like' | 'pass', index: number}>} */
  history = [];
  
  /** @type {Array<{id: string, variantId: string, title: string, image: string}>} */
  likedProducts = [];

  // Swipe state
  isDragging = false;
  startX = 0;
  startY = 0;
  offsetX = 0;
  offsetY = 0;
  swipeBlocked = false;

  // Constants
  SWIPE_THRESHOLD = 80;
  MAX_ROTATION = 12;
  MILESTONE_1 = 3;
  MILESTONE_2 = 7;

  /** @type {HTMLElement | null} */
  activeCard = null;
  
  /** @type {boolean} */
  isAnimating = false;

  connectedCallback() {
    super.connectedCallback();
    
    const sectionId = this.dataset.sectionId || 'default';
    this.stateManager = new TestifyState(sectionId);
    
    this.#initializeQuestions();
    this.#initializeProducts();
    this.#bindEvents();
    this.#bindActionButtons();
    
    window.addEventListener('beforeunload', this.#saveState);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.#saveState();
    });
    
    this.addEventListener('click', (e) => {
      if (e.target.closest('a')) this.#saveState();
    });
  }

  #bindActionButtons() {
    // Direct event listeners for all action buttons
    const restartBtn = this.querySelector('.restart-btn');
    if (restartBtn) {
      restartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleRestart();
      });
    }

    const likeBtn = this.querySelector('.action-btn--like');
    if (likeBtn) {
      likeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleLike();
      });
    }

    const passBtn = this.querySelector('.action-btn--pass');
    if (passBtn) {
      passBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handlePass();
      });
    }

    const rewindBtn = this.querySelector('.action-btn--rewind');
    if (rewindBtn) {
      rewindBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleRewind();
      });
    }
  }

  #getLikedStackElement() {
    // Liked stack is now inside the component
    return this.querySelector('.liked-stack');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#saveState();
    document.removeEventListener('mousemove', this.#handleMouseMove);
    document.removeEventListener('mouseup', this.#handleMouseUp);
    document.removeEventListener('touchmove', this.#handleTouchMove);
    document.removeEventListener('touchend', this.#handleTouchEnd);
    window.removeEventListener('beforeunload', this.#saveState);
  }

  #saveState = () => {
    this.stateManager.save({
      currentQuestionIndex: this.currentQuestionIndex,
      collectedKeywords: this.collectedKeywords,
      questionnaireComplete: this.questionnaireComplete,
      currentIndex: this.currentIndex,
      history: this.history,
      likedProducts: this.likedProducts,
      filteredProducts: this.filteredProducts,
    });
  };

  #restoreState() {
    const saved = this.stateManager.load();
    if (!saved) return false;
    
    this.currentQuestionIndex = saved.currentQuestionIndex || 0;
    this.collectedKeywords = saved.collectedKeywords || [];
    this.questionnaireComplete = saved.questionnaireComplete || false;
    this.currentIndex = saved.currentIndex || 0;
    this.history = saved.history || [];
    this.likedProducts = saved.likedProducts || [];
    
    if (saved.filteredProducts && saved.filteredProducts.length > 0) {
      this.filteredProducts = saved.filteredProducts;
    }
    
    return true;
  }

  #initializeQuestions() {
    const questionsData = this.dataset.questions;
    if (questionsData) {
      try {
        this.questions = JSON.parse(questionsData);
      } catch (e) {
        console.error('Sorular ayrıştırılamadı:', e);
        this.questions = [];
      }
    }
  }

  #initializeProducts() {
    const productsData = this.dataset.products;
    
    if (productsData && productsData.length > 2) {
      try {
        const parsed = JSON.parse(productsData);
        this.allProducts = Array.isArray(parsed) 
          ? parsed.filter(p => p && typeof p === 'object' && p.id)
          : [];
      } catch (e) {
        console.error('Ürün verileri yüklenemedi:', e.message);
        this.allProducts = [];
      }
    } else {
      this.allProducts = [];
    }

    const restored = this.#restoreState();
    
    if (restored && this.questionnaireComplete) {
      this.#showProductPhase();
      this.#renderProductCards();
      this.#updateLikedStack();
      this.#updateUI();
    } else if (restored && this.currentQuestionIndex > 0) {
      // Restore mid-questionnaire
      if (this.currentQuestionIndex >= this.questions.length) {
        this.#completeQuestionnaire();
      } else {
        this.#renderQuestionCard();
      }
    } else {
      // Fresh start - hide card info during questionnaire
      const cardInfo = this.querySelector('.card-info');
      if (cardInfo) cardInfo.hidden = true;
      
      if (this.questions.length > 0) {
        this.#renderQuestionCard();
      } else {
        // No questions, go straight to products
        this.filteredProducts = this.allProducts.filter(p => p && p.id && p.title && p.variantId);
        this.questionnaireComplete = true;
        this.#showProductPhase();
        this.#renderProductCards();
        this.#updateUI();
      }
    }
  }

  #bindEvents() {
    this.refs.cardStack?.addEventListener('touchstart', this.#handleTouchStart, { passive: false });
    document.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    document.addEventListener('touchend', this.#handleTouchEnd);

    this.refs.cardStack?.addEventListener('mousedown', this.#handleMouseDown);
    document.addEventListener('mousemove', this.#handleMouseMove);
    document.addEventListener('mouseup', this.#handleMouseUp);
  }

  // ============== QUESTIONNAIRE METHODS ==============

  #renderQuestionCard() {
    const cardStack = this.refs.cardStack;
    if (!cardStack) return;

    cardStack.innerHTML = '';
    
    if (this.currentQuestionIndex >= this.questions.length) {
      this.#completeQuestionnaire();
      return;
    }

    const question = this.questions[this.currentQuestionIndex];
    const card = this.#createQuestionCard(question);
    cardStack.appendChild(card);

    // Update question progress
    this.#updateQuestionProgress();

    // Block swiping for multiple choice
    this.swipeBlocked = question.type === 'multiple_choice';
  }

  #createQuestionCard(question) {
    const card = document.createElement('div');
    card.className = 'swipe-card swipe-card--question active';
    card.dataset.questionId = question.id;
    card.dataset.questionType = question.type;

    if (question.type === 'yes_no') {
      card.innerHTML = `
        <div class="swipe-card__inner">
          <div class="question-card">
            <div class="question-card__badge">Soru ${this.currentQuestionIndex + 1}/${this.questions.length}</div>
            <div class="question-card__content">
              <h2 class="question-card__text">${question.question}</h2>
              <div class="question-card__hint">
                <span class="hint hint--no"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> Hayır</span>
                <span class="hint hint--yes"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 6L9 17l-5-5"/></svg> Evet</span>
              </div>
            </div>
            <div class="swipe-indicator swipe-indicator--like">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 6L9 17l-5-5"/></svg>
              <span>EVET</span>
            </div>
            <div class="swipe-indicator swipe-indicator--pass">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              <span>HAYIR</span>
            </div>
          </div>
        </div>
      `;
    } else {
      // Multiple choice
      const optionsHtml = question.options.map((opt, idx) => `
        <button type="button" class="choice-btn" data-index="${idx}" on:click="/handleChoiceSelect?index=${idx}">
          <span class="choice-btn__letter">${String.fromCharCode(65 + idx)}</span>
          <span class="choice-btn__text">${opt.text}</span>
        </button>
      `).join('');

      card.innerHTML = `
        <div class="swipe-card__inner">
          <div class="question-card question-card--choices">
            <div class="question-card__badge">Soru ${this.currentQuestionIndex + 1}/${this.questions.length}</div>
            <div class="question-card__content">
              <h2 class="question-card__text">${question.question}</h2>
              <div class="choice-options">
                ${optionsHtml}
              </div>
            </div>
          </div>
        </div>
      `;
    }

    return card;
  }

  #updateQuestionProgress() {
    const section = this.closest('.product-testify-section');
    const progressEl = section?.querySelector('.question-progress');
    const progressFill = section?.querySelector('.question-progress__fill');
    const progressText = section?.querySelector('.question-progress__text');
    
    if (progressEl) progressEl.hidden = false;
    
    if (progressFill) {
      // Progress fills as questions are answered (currentQuestionIndex + 1 because we show progress for current question)
      const progress = ((this.currentQuestionIndex + 1) / this.questions.length) * 100;
      progressFill.style.width = `${progress}%`;
    }
    
    if (progressText) {
      progressText.textContent = `${this.currentQuestionIndex + 1} / ${this.questions.length}`;
    }
  }

  handleChoiceSelect(data, e) {
    if (this.isAnimating) return;
    
    const index = typeof data === 'object' ? data.index : data;
    const question = this.questions[this.currentQuestionIndex];
    
    if (question && question.options && question.options[index]) {
      const selectedOption = question.options[index];
      
      // Collect keywords from selected option
      if (selectedOption.keywords && selectedOption.keywords.length > 0) {
        this.collectedKeywords.push(...selectedOption.keywords);
      }
      
      // Animate selection
      const btn = e?.target?.closest('.choice-btn');
      if (btn) {
        btn.classList.add('selected');
      }
      
      this.isAnimating = true;
      
      setTimeout(() => {
        this.currentQuestionIndex++;
        this.isAnimating = false;
        this.#renderQuestionCard();
        this.#saveState();
      }, 400);
    }
  }

  #handleYesNoAnswer(answer) {
    const question = this.questions[this.currentQuestionIndex];
    if (!question) return;
    
    // Collect keywords based on answer
    if (answer === 'yes' && question.yesKeywords) {
      this.collectedKeywords.push(...question.yesKeywords);
    } else if (answer === 'no' && question.noKeywords) {
      this.collectedKeywords.push(...question.noKeywords);
    }
    
    this.currentQuestionIndex++;
    this.#saveState();
  }

  #completeQuestionnaire() {
    this.questionnaireComplete = true;
    
    // Filter products based on collected keywords
    this.#filterProductsByKeywords();
    
    // Show transition
    this.#showTransition();
  }

  #filterProductsByKeywords() {
    // First, filter out any null or invalid products
    const validProducts = this.allProducts.filter(product => 
      product && product.id && product.title && product.variantId
    );

    if (this.collectedKeywords.length === 0) {
      this.filteredProducts = [...validProducts];
      return;
    }

    // Normalize keywords for comparison
    const normalizedKeywords = this.collectedKeywords
      .filter(k => k && typeof k === 'string')
      .map(k => k.toLowerCase().trim())
      .filter(k => k.length > 0);

    if (normalizedKeywords.length === 0) {
      this.filteredProducts = [...validProducts];
      return;
    }
    
    // Filter products that have at least one matching tag
    this.filteredProducts = validProducts.filter(product => {
      if (!product.tags || !Array.isArray(product.tags) || product.tags.length === 0) return false;
      
      const productTags = product.tags
        .filter(t => t && typeof t === 'string')
        .map(t => t.toLowerCase().trim());
      
      return normalizedKeywords.some(keyword => 
        productTags.some(tag => tag === keyword || tag.includes(keyword))
      );
    });

    // If no products match, show all valid products
    if (this.filteredProducts.length === 0) {
      this.filteredProducts = [...validProducts];
    }
  }

  #showTransition() {
    const cardStack = this.refs.cardStack;
    if (!cardStack) return;

    // Hide question progress
    const section = this.closest('.product-testify-section');
    const questionProgress = section?.querySelector('.question-progress');
    if (questionProgress) questionProgress.hidden = true;

    // Phase 1: "Analyzing your preferences"
    cardStack.innerHTML = `
      <div class="transition-card">
        <div class="transition-card__icon transition-card__icon--pulse">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
        </div>
        <h2 class="transition-card__title">Senin için en iyi seçimleri yapıyoruz</h2>
        <p class="transition-card__subtitle">Tercihlerini analiz ediyoruz...</p>
        <div class="transition-card__progress">
          <div class="transition-card__progress-bar"></div>
        </div>
      </div>
    `;

    // Phase 2: "Finding products" (after 1.5s)
    setTimeout(() => {
      const subtitle = cardStack.querySelector('.transition-card__subtitle');
      if (subtitle) subtitle.textContent = 'Ürünleri eşleştiriyoruz...';
    }, 1500);

    // Phase 3: "Preparing results" (after 3s)
    setTimeout(() => {
      const subtitle = cardStack.querySelector('.transition-card__subtitle');
      const icon = cardStack.querySelector('.transition-card__icon');
      if (subtitle) subtitle.textContent = 'Sonuçlar hazırlanıyor...';
      if (icon) {
        icon.classList.remove('transition-card__icon--pulse');
        icon.classList.add('transition-card__icon--check');
        icon.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>`;
      }
    }, 3500);

    // Final: Show products (after 4.5s)
    setTimeout(() => {
      this.#showProductPhase();
      this.#renderProductCards();
      this.#updateLikedStack();
      this.#updateUI();
      this.#saveState();
    }, 4500);
  }

  #showProductPhase() {
    const section = this.closest('.product-testify-section');
    
    // Hide question progress
    const questionProgress = section?.querySelector('.question-progress');
    if (questionProgress) questionProgress.hidden = true;
    
    // Show product progress
    const progressContainer = section?.querySelector('.progress-container');
    if (progressContainer) progressContainer.hidden = false;
    
    // Show card info (products remaining)
    const cardInfo = this.querySelector('.card-info');
    if (cardInfo) cardInfo.hidden = false;
    
    // Update header
    const headerText = section?.querySelector('.testify-header__text p');
    if (headerText) headerText.textContent = 'Beğendiğin ürünleri sepete ekle';
  }

  // ============== SWIPE HANDLERS ==============

  #handleTouchStart = (e) => {
    if (this.isAnimating || this.swipeBlocked) return;
    if (!this.questionnaireComplete && this.questions[this.currentQuestionIndex]?.type === 'multiple_choice') return;
    
    const touch = e.touches[0];
    this.#startDrag(touch.clientX, touch.clientY);
  };

  #handleTouchMove = (e) => {
    if (!this.isDragging) return;
    e.preventDefault();
    const touch = e.touches[0];
    this.#moveDrag(touch.clientX, touch.clientY);
  };

  #handleTouchEnd = () => {
    if (!this.isDragging) return;
    this.#endDrag();
  };

  #handleMouseDown = (e) => {
    if (this.isAnimating || this.swipeBlocked) return;
    if (!this.questionnaireComplete && this.questions[this.currentQuestionIndex]?.type === 'multiple_choice') return;
    if (e.target.closest('a, button')) return;
    
    e.preventDefault();
    this.#startDrag(e.clientX, e.clientY);
  };

  #handleMouseMove = (e) => {
    if (!this.isDragging) return;
    this.#moveDrag(e.clientX, e.clientY);
  };

  #handleMouseUp = () => {
    if (!this.isDragging) return;
    this.#endDrag();
  };

  #startDrag(x, y) {
    this.activeCard = this.refs.cardStack?.querySelector('.swipe-card.active');
    if (!this.activeCard) return;

    this.isDragging = true;
    this.startX = x;
    this.startY = y;
    this.offsetX = 0;
    this.offsetY = 0;

    this.activeCard.classList.add('dragging');
  }

  #moveDrag(x, y) {
    if (!this.activeCard) return;

    this.offsetX = x - this.startX;
    this.offsetY = (y - this.startY) * 0.4;

    const rotation = (this.offsetX / window.innerWidth) * this.MAX_ROTATION;
    
    this.activeCard.style.transform = `translate3d(${this.offsetX}px, ${this.offsetY}px, 0) rotate(${rotation}deg)`;
    
    const likeIndicator = this.activeCard.querySelector('.swipe-indicator--like');
    const passIndicator = this.activeCard.querySelector('.swipe-indicator--pass');
    
    if (likeIndicator && passIndicator) {
      const progress = Math.min(Math.abs(this.offsetX) / this.SWIPE_THRESHOLD, 1);
      if (this.offsetX > 20) {
        likeIndicator.style.opacity = progress.toString();
        passIndicator.style.opacity = '0';
      } else if (this.offsetX < -20) {
        passIndicator.style.opacity = progress.toString();
        likeIndicator.style.opacity = '0';
      } else {
        likeIndicator.style.opacity = '0';
        passIndicator.style.opacity = '0';
      }
    }
  }

  #endDrag() {
    if (!this.activeCard) return;

    this.isDragging = false;
    this.activeCard.classList.remove('dragging');

    const shouldSwipeRight = this.offsetX > this.SWIPE_THRESHOLD;
    const shouldSwipeLeft = this.offsetX < -this.SWIPE_THRESHOLD;

    if (shouldSwipeRight) {
      this.#swipeCard('right');
    } else if (shouldSwipeLeft) {
      this.#swipeCard('left');
    } else {
      this.activeCard.style.transition = 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
      this.activeCard.style.transform = 'translate3d(0, 0, 0) rotate(0deg)';
      
      const likeIndicator = this.activeCard.querySelector('.swipe-indicator--like');
      const passIndicator = this.activeCard.querySelector('.swipe-indicator--pass');
      if (likeIndicator) likeIndicator.style.opacity = '0';
      if (passIndicator) passIndicator.style.opacity = '0';
      
      setTimeout(() => {
        if (this.activeCard) this.activeCard.style.transition = '';
      }, 400);
    }
  }

  #swipeCard(direction) {
    if (!this.activeCard || this.isAnimating) return;
    
    this.isAnimating = true;
    const card = this.activeCard;
    
    const exitX = direction === 'right' ? window.innerWidth : -window.innerWidth;
    const exitRotation = direction === 'right' ? 20 : -20;

    const indicator = card.querySelector(direction === 'right' ? '.swipe-indicator--like' : '.swipe-indicator--pass');
    if (indicator) indicator.style.opacity = '1';

    card.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease';
    card.style.transform = `translate3d(${exitX}px, ${this.offsetY}px, 0) rotate(${exitRotation}deg)`;
    card.style.opacity = '0';
    card.style.pointerEvents = 'none';

    setTimeout(() => {
      card.remove();
      
      if (!this.questionnaireComplete) {
        // Questionnaire phase - handle yes/no
        this.#handleYesNoAnswer(direction === 'right' ? 'yes' : 'no');
        this.#renderQuestionCard();
      } else {
        // Product phase
        if (direction === 'right') {
          this.#processLike();
        } else {
          this.#processPass();
        }
        this.currentIndex++;
        this.#activateNextCard();
        this.#updateUI();
      }
      
      this.isAnimating = false;
      this.#saveState();
    }, 500);
  }

  // ============== PRODUCT METHODS ==============

  #renderProductCards() {
    const cardStack = this.refs.cardStack;
    if (!cardStack) return;

    cardStack.innerHTML = '';

    const visibleProducts = this.filteredProducts.slice(this.currentIndex, this.currentIndex + 3);
    
    if (visibleProducts.length === 0) {
      this.#showEmptyState();
      return;
    }

    this.#hideEmptyState();

    visibleProducts.forEach((product, index) => {
      if (!product) return;
      const card = this.#createProductCard(product, index);
      if (index === 0) card.classList.add('active');
      cardStack.appendChild(card);
    });
  }

  #createProductCard(product, stackIndex) {
    const card = document.createElement('div');
    card.className = 'swipe-card';
    card.dataset.productId = product.id;
    card.style.setProperty('--stack-index', stackIndex.toString());
    
    const hasComparePrice = product.comparePrice && product.comparePrice !== product.price;

    card.innerHTML = `
      <div class="swipe-card__inner">
        <div class="swipe-card__image-wrapper">
          ${product.image 
            ? `<img src="${product.image}" alt="${product.title}" class="swipe-card__image" loading="eager" draggable="false" />`
            : `<div class="swipe-card__placeholder"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></div>`
          }
          <div class="swipe-indicator swipe-indicator--like">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
            <span>SEPETE EKLE</span>
          </div>
          <div class="swipe-indicator swipe-indicator--pass">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            <span>GEÇ</span>
          </div>
        </div>
        <div class="swipe-card__content">
          <h3 class="swipe-card__title">${product.title}</h3>
          <div class="swipe-card__price-row">
            <span class="swipe-card__price">${product.price}</span>
            ${hasComparePrice ? `<span class="swipe-card__compare-price">${product.comparePrice}</span>` : ''}
          </div>
          <a href="${product.url}" class="swipe-card__link">
            <span>Ürünü İncele</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </a>
        </div>
      </div>
    `;

    return card;
  }

  #activateNextCard() {
    const cards = this.refs.cardStack?.querySelectorAll('.swipe-card');
    if (!cards || cards.length === 0) {
      this.#showEmptyState();
      return;
    }

    cards.forEach((card, index) => {
      card.style.setProperty('--stack-index', index.toString());
      if (index === 0) {
        card.classList.add('active');
        card.style.transform = '';
        card.style.opacity = '';
        card.style.transition = '';
      } else {
        card.classList.remove('active');
      }
    });

    const nextProductIndex = this.currentIndex + cards.length;
    if (nextProductIndex < this.filteredProducts.length) {
      const product = this.filteredProducts[nextProductIndex];
      const newCard = this.#createProductCard(product, cards.length);
      this.refs.cardStack?.appendChild(newCard);
    }
  }

  #processLike() {
    const product = this.filteredProducts[this.currentIndex];
    if (!product) return;

    this.history.push({ 
      productId: product.id, 
      variantId: product.variantId, 
      action: 'like',
      index: this.currentIndex 
    });
    
    this.likedProducts.push({
      id: product.id,
      variantId: product.variantId,
      title: product.title,
      image: product.image,
    });
    
    this.#updateLikedStack();
    this.#addToCart(product.variantId);
    this.#checkMilestone();
  }

  #processPass() {
    const product = this.filteredProducts[this.currentIndex];
    if (!product) return;

    this.history.push({ 
      productId: product.id, 
      variantId: product.variantId, 
      action: 'pass',
      index: this.currentIndex 
    });
  }

  #checkMilestone() {
    const count = this.likedProducts.length;
    if (count === this.MILESTONE_1 || count === this.MILESTONE_2) {
      this.#showMilestoneReached(count === this.MILESTONE_1 ? 1 : 2);
    }
  }

  #showMilestoneReached(milestone) {
    const toast = document.createElement('div');
    toast.className = 'milestone-toast';
    toast.innerHTML = milestone === 1 
      ? `<span class="milestone-toast__icon">🎉</span><span>3 Al 2 Öde kazandın!</span>`
      : `<span class="milestone-toast__icon">🎁</span><span>7 Al 4 Öde kazandın!</span>`;
    
    this.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('show'));
    
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 400);
    }, 3000);
  }

  async #addToCart(variantId) {
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript', { body: formData }),
        headers: { Accept: 'application/json' },
      });

      const data = await response.json();
      if (!data.status) {
        this.dispatchEvent(new CartAddEvent({}, 'product-testify', { source: 'product-testify', variantId }));
      }
    } catch (error) {
      console.error('Sepete eklenirken hata:', error);
    }
  }

  // ============== BUTTON HANDLERS ==============

  handleLike() {
    if (this.isAnimating || !this.questionnaireComplete || this.currentIndex >= this.filteredProducts.length) return;
    this.activeCard = this.refs.cardStack?.querySelector('.swipe-card.active');
    this.offsetY = 0;
    this.#swipeCard('right');
  }

  handlePass() {
    if (this.isAnimating || !this.questionnaireComplete || this.currentIndex >= this.filteredProducts.length) return;
    this.activeCard = this.refs.cardStack?.querySelector('.swipe-card.active');
    this.offsetY = 0;
    this.#swipeCard('left');
  }

  handleRewind() {
    if (this.history.length === 0 || this.isAnimating || !this.questionnaireComplete) return;

    const lastAction = this.history.pop();
    if (!lastAction) return;

    this.isAnimating = true;
    this.currentIndex--;

    if (lastAction.action === 'like') {
      this.#removeFromCart(lastAction.variantId);
      this.likedProducts = this.likedProducts.filter(p => p.id !== lastAction.productId);
      this.#updateLikedStack();
    }

    const cards = this.refs.cardStack?.querySelectorAll('.swipe-card');
    if (cards && cards.length > 0) cards[cards.length - 1]?.remove();

    const product = this.filteredProducts[this.currentIndex];
    const newCard = this.#createProductCard(product, 0);
    newCard.classList.add('active');
    
    const startX = lastAction.action === 'like' ? window.innerWidth : -window.innerWidth;
    newCard.style.transform = `translate3d(${startX}px, 0, 0) rotate(${lastAction.action === 'like' ? 20 : -20}deg)`;
    newCard.style.opacity = '0';
    
    this.refs.cardStack?.prepend(newCard);

    this.refs.cardStack?.querySelectorAll('.swipe-card').forEach((card, index) => {
      card.style.setProperty('--stack-index', index.toString());
      card.classList.toggle('active', index === 0);
    });

    requestAnimationFrame(() => {
      newCard.style.transition = 'transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.3s ease';
      newCard.style.transform = 'translate3d(0, 0, 0) rotate(0deg)';
      newCard.style.opacity = '1';
    });

    setTimeout(() => {
      newCard.style.transition = '';
      this.isAnimating = false;
      this.#saveState();
    }, 500);

    this.#hideEmptyState();
    this.#updateUI();
  }

  handleRestart() {
    // Clear saved state
    this.stateManager.clear();
    
    // Reset all state variables
    this.currentQuestionIndex = 0;
    this.collectedKeywords = [];
    this.questionnaireComplete = false;
    this.currentIndex = 0;
    this.history = [];
    this.likedProducts = [];
    this.filteredProducts = [];
    this.isAnimating = false;
    this.swipeBlocked = false;
    
    // Reset UI elements
    const section = this.closest('.product-testify-section');
    
    // Hide product progress, show question progress
    const progressContainer = section?.querySelector('.progress-container');
    if (progressContainer) progressContainer.hidden = true;
    
    const questionProgress = section?.querySelector('.question-progress');
    if (questionProgress) questionProgress.hidden = false;
    
    // Reset question progress bar
    const progressFill = section?.querySelector('.question-progress__fill');
    if (progressFill) progressFill.style.width = '0%';
    
    // Hide card info during questionnaire
    const cardInfo = this.querySelector('.card-info');
    if (cardInfo) cardInfo.hidden = true;
    
    // Hide empty state
    this.#hideEmptyState();
    
    // Render first question if we have questions, otherwise show products
    if (this.questions.length > 0) {
      this.#renderQuestionCard();
    } else {
      this.filteredProducts = this.allProducts.filter(p => p && p.id && p.title && p.variantId);
      this.questionnaireComplete = true;
      this.#showProductPhase();
      this.#renderProductCards();
    }
    
    this.#updateLikedStack();
    this.#updateMobileCounter();
  }

  async #removeFromCart(variantId) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 0 }),
      });
      this.dispatchEvent(new CartAddEvent({}, 'product-testify', { source: 'product-testify', variantId }));
    } catch (error) {
      console.error('Sepetten çıkarılırken hata:', error);
    }
  }

  // ============== UI UPDATES ==============

  #updateLikedStack() {
    const likedStack = this.#getLikedStackElement();
    if (!likedStack) return;

    if (this.likedProducts.length === 0) {
      likedStack.innerHTML = `
        <div class="liked-stack__empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          <span>Beğendiğin ürünler burada görünecek</span>
        </div>
      `;
      return;
    }

    likedStack.innerHTML = '';
    const displayProducts = this.likedProducts.slice(-4);
    
    displayProducts.forEach((product, index) => {
      const item = document.createElement('div');
      item.className = 'liked-stack__item';
      item.style.setProperty('--stack-offset', (displayProducts.length - 1 - index).toString());
      item.innerHTML = `<img src="${product.image}" alt="${product.title}" draggable="false" />`;
      likedStack.appendChild(item);
    });

    const counter = document.createElement('div');
    counter.className = 'liked-stack__counter';
    counter.innerHTML = `<span>${this.likedProducts.length}</span>`;
    likedStack.appendChild(counter);
  }

  #updateUI() {
    this.#updateRewindButton();
    this.#updateCounter();
    this.#updateProgressBar();
    this.#updateMobileCounter();
  }

  #updateRewindButton() {
    const rewindBtn = this.refs.rewindBtn;
    if (rewindBtn) {
      const canRewind = this.history.length > 0 && this.questionnaireComplete;
      rewindBtn.disabled = !canRewind;
      rewindBtn.classList.toggle('disabled', !canRewind);
    }
  }

  #updateCounter() {
    const counter = this.refs.currentCardInfo;
    if (counter && this.questionnaireComplete) {
      const remaining = Math.max(0, this.filteredProducts.length - this.currentIndex);
      counter.textContent = `${remaining} ürün kaldı`;
    }
  }

  #updateProgressBar() {
    if (!this.questionnaireComplete) return;
    
    const section = this.closest('.product-testify-section');
    if (!section) return;
    
    const progressFill = section.querySelector('.progress-bar__fill');
    const milestone1El = section.querySelector('.progress-milestone[data-milestone="1"]');
    const milestone2El = section.querySelector('.progress-milestone[data-milestone="2"]');
    const progressText = section.querySelector('.progress-text');
    const label1 = section.querySelector('.progress-label[data-for="1"]');
    const label2 = section.querySelector('.progress-label[data-for="2"]');
    
    const count = this.likedProducts.length;
    const progress = Math.min((count / this.MILESTONE_2) * 100, 100);
    
    if (progressFill) progressFill.style.width = `${progress}%`;
    
    if (milestone1El) {
      milestone1El.classList.toggle('reached', count >= this.MILESTONE_1);
      milestone1El.classList.toggle('active', count > 0 && count < this.MILESTONE_1);
    }
    
    if (milestone2El) {
      milestone2El.classList.toggle('reached', count >= this.MILESTONE_2);
      milestone2El.classList.toggle('active', count >= this.MILESTONE_1 && count < this.MILESTONE_2);
    }
    
    if (label1) label1.classList.toggle('reached', count >= this.MILESTONE_1);
    if (label2) label2.classList.toggle('reached', count >= this.MILESTONE_2);
    
    if (progressText) {
      if (count >= this.MILESTONE_2) {
        progressText.textContent = 'Tüm ödülleri kazandın! 🎉';
      } else if (count >= this.MILESTONE_1) {
        progressText.textContent = `7 Al 4 Öde için ${this.MILESTONE_2 - count} ürün daha ekle!`;
      } else {
        progressText.textContent = `3 Al 2 Öde için ${this.MILESTONE_1 - count} ürün daha ekle!`;
      }
    }
  }

  #updateMobileCounter() {
    const mobileCounter = this.refs.mobileCounter;
    if (mobileCounter) {
      const count = this.likedProducts.length;
      mobileCounter.textContent = count.toString();
      mobileCounter.classList.toggle('has-items', count > 0);
    }
  }

  #showEmptyState() {
    const emptyState = this.refs.emptyState;
    if (emptyState) emptyState.hidden = false;
  }

  #hideEmptyState() {
    const emptyState = this.refs.emptyState;
    if (emptyState) emptyState.hidden = true;
  }
}

if (!customElements.get('product-testify')) {
  customElements.define('product-testify', ProductTestify);
}
